import React, { useEffect, useState, useMemo } from 'react';
import { couponDistributionRequestsAPI, formatCurrency, formatDate } from '../../services/api';
import { Loader, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const getNumericValue = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === 'object') {
    if ('$numberDecimal' in value) {
      const parsed = Number(value.$numberDecimal);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if ('$numberDouble' in value) {
      const parsed = Number(value.$numberDouble);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }
  return 0;
};

const RequestStatusBadge = ({ status }) => {
  const normalized = status?.toLowerCase();
  const config = {
    pending: { text: 'Pending L1', className: 'bg-yellow-100 text-yellow-800' },
    pending_l2: { text: 'Pending L2', className: 'bg-orange-100 text-orange-800' },
    approved: { text: 'Approved', className: 'bg-green-100 text-green-800' },
    rejected: { text: 'Rejected', className: 'bg-red-100 text-red-800' }
  }[normalized] || { text: status, className: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.text}
    </span>
  );
};

const CustodianCouponDistributionRequests = () => {
  const { user } = useAuth();
  const currentUserId = user?._id || user?.id;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvingId, setApprovingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await couponDistributionRequestsAPI.getMyRequests({ status: statusFilter });
      const data = response.data?.data || response.data || [];
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleApprove = async (request) => {
    try {
      setApprovingId(request._id);
      if (request.status === 'pending') {
        await couponDistributionRequestsAPI.approveRequestL1(request._id);
      } else if (request.status === 'pending_l2') {
        await couponDistributionRequestsAPI.approveRequestL2(request._id);
      }
      await fetchRequests();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to approve request.');
    } finally {
      setApprovingId(null);
    }
  };

  const getUserIdValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value._id || value.id || null;
  };

  const canApproveL2 = (request) => {
    if (request.status !== 'pending_l2') return true;
    const l1ApproverId = getUserIdValue(request.l1ApprovedBy);
    if (!l1ApproverId) return true;
    if (!currentUserId) return false;
    return l1ApproverId.toString() !== currentUserId.toString();
  };

  const getActionLabel = (request) => {
    if (request.status === 'pending_l2') {
      return 'Approve L2';
    }
    return 'Approve';
  };

  const filteredRequests = useMemo(() => {
    if (!searchTerm) {
      return requests;
    }
    const keyword = searchTerm.toLowerCase();
    return requests.filter((request) => {
      const bondName = request.bondId?.bondName?.toLowerCase() || '';
      const isin = request.bondId?.isin?.toLowerCase() || '';
      const paymentType = (request.paymentType || '').toLowerCase();
      const status = (request.status || '').toLowerCase();
      return (
        bondName.includes(keyword) ||
        isin.includes(keyword) ||
        paymentType.includes(keyword) ||
        status.includes(keyword)
      );
    });
  }, [requests, searchTerm]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-8 mx-auto space-y-6 max-w-7xl sm:px-6 lg:px-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Coupon Payment Requests</h1>
        <p className="mt-1 text-sm text-gray-600">Review and approve incoming coupon payment requests</p>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-6 lg:grid-cols-3">
        <div className="card lg:col-span-2">
          <div className="card-body">
            <div className="relative">
              <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-dark-gray" />
              <input
                type="text"
                placeholder="Search bonds by name, ISIN, payment type, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 form-input"
              />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">All</option>
              <option value="pending">Pending L1</option>
              <option value="pending_l2">Pending L2</option>
              <option value="approved">Approved</option>
            </select>
          </div>
        </div>
      </div>

        <div className="p-6 bg-white border border-gray-200 rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader className="w-5 h-5 mr-2 animate-spin" />
            Loading requests...
          </div>
        ) : error ? (
          <div className="py-8 text-sm text-center text-red-600">{error}</div>
        ) : filteredRequests.length === 0 ? (
          <div className="py-12 text-sm text-center text-gray-500">No requests found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Payment Date</th>
                  <th className="table-header-cell">Bond</th>
                  <th className="table-header-cell">Payment Type</th>
                  <th className="table-header-cell">Bond Amount</th>
                  <th className="table-header-cell">Amount (MYR)</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Action</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {filteredRequests.map((request) => (
                  <tr key={request._id}>
                    <td className="table-cell">{formatDate(request.paymentDate)}</td>
                    <td className="table-cell">
                      <div className="font-medium">{request.bondId?.bondName || 'Unknown Bond'}</div>
                      <div className="text-xs text-gray-500">{request.bondId?.isin || request.bondId?.bondNumber}</div>
                    </td>
                    <td className="table-cell">
                      {request.paymentType || 'Profit'}
                    </td>
                    <td className="table-cell">
                      {getNumericValue(request.tokenAmount).toLocaleString()}
                    </td>
                    <td className="table-cell">
                      {formatCurrency(getNumericValue(request.amount))}
                    </td>
                    <td className="table-cell">
                      <RequestStatusBadge status={request.status} />
                    </td>
                    <td className="px-4 py-3 text-sm ">
                      {request.status === 'approved' ? (
                        <span className="text-xs text-gray-500">Approved</span>
                      ) : (
                        <button
                          onClick={() => handleApprove(request)}
                          disabled={
                            approvingId === request._id ||
                            (request.status === 'pending_l2' && !canApproveL2(request))
                          }
                          className="btn-primary btn-sm"
                        >
                          {approvingId === request._id ? 'Approving...' : getActionLabel(request)}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

export default CustodianCouponDistributionRequests;

